import React from "react";
import "./AccountModal.styles.scss";

// Component Imports
import DeleteModal from "@/components/Modals/DeleteModal/DeleteModal";

// MUI Imports
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";

// Type Imports
import { IAlertMessage } from "@pulse-ui/types/components/AlertMessage";
import { IAGPortfolioDataRow } from "@pulse-ui/types/components/AGTable";

// Local Components
import { AccountDetailsSection } from "./components/AccountDetailsSection/AccountDetailsSection";
import { ApiKeySection } from "./components/ApiKeySection/ApiKeySection";
import { useAccountModal } from "./hooks/useAccountModal";

const CustomDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

export interface DialogTitleProps {
  className: string;
  children?: React.ReactNode;
  onClose: () => void;
}

function CustomDialogTitle(props: DialogTitleProps) {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 2.5, pr: 7 }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "#ebe93e",
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
}

const AccountModal = ({
  account_details,
  portfolio_id,
  dataMap,
  setDataMap,
  setLoading,
  pushAlert,
  setSelectedAccountDetails,
  fetchVenueAccountsForExpandedRows,
}: {
  account_details: any;
  portfolio_id: string;
  dataMap: Map<string, IAGPortfolioDataRow>;
  setDataMap: React.Dispatch<
    React.SetStateAction<Map<string, IAGPortfolioDataRow>>
  >;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  pushAlert: (alert: IAlertMessage) => void;
  setSelectedAccountDetails: React.Dispatch<
    React.SetStateAction<string | null>
  >;
  fetchVenueAccountsForExpandedRows: (
    portfolios_map: Map<string, IAGPortfolioDataRow>
  ) => Promise<Map<string, IAGPortfolioDataRow>>;
}) => {
  const {
    // State
    selectedApiKey,
    setSelectedApiKey,
    displayApiKeyForm,
    setDisplayApiKeyForm,
    newApiKey,
    isAccountEditMode,
    setIsAccountEditMode,
    buttonDisableStatus,
    deleteModalStatus,
    setDeleteModalStatus,
    accountForm,
    setAccountForm,
    clipboardSnackbar,
    // Loading state from RTK Query mutations
    isLoading: isAccountModalLoading,
    // Data
    apiKeys,
    venueCodes,
    ipAddresses,
    strategies,
    // Methods
    copyToClipboard,
    handleCloseSnackbar,
    handleClose,
    handleAccountFormChange,
    handleSaveAccountEdits,
    handleDeleteApiKey,
    handleSaveApiKey,
    handleNewApiKeyChange,
    handleNewApiKeyFreeSoloChange,
    handleNewApiKeyVenueApiKeyFlagChange,
    handleNewApiKeyNamespacesChange,
    handleNewApiKeyCapabilitiesChange,
  } = useAccountModal({
    account_details,
    portfolio_id,
    dataMap,
    setDataMap,
    pushAlert,
    setSelectedAccountDetails,
    fetchVenueAccountsForExpandedRows,
  });

  // Notify parent component of loading state changes
  React.useEffect(() => {
    setLoading(isAccountModalLoading);
  }, [isAccountModalLoading, setLoading]);

  return (
    <div className="account-modal">
      <CustomDialog
        onClose={handleClose}
        open
        className="account-modal-dialog"
        maxWidth="xl"
        fullWidth
        PaperProps={{
          elevation: 0,
          sx: {
            boxShadow: "0px 0px 4px #777",
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.5)",
            },
          },
        }}
      >
        <CustomDialogTitle
          className="account-modal-dialog-title"
          onClose={handleClose}
        >
          {account_details.display_name}
        </CustomDialogTitle>
        <DialogContent dividers className="account-modal-dialog-content">
          <AccountDetailsSection
            accountForm={accountForm}
            strategies={strategies}
            isEditMode={isAccountEditMode}
            onToggleEditMode={() => setIsAccountEditMode((prev) => !prev)}
            onSave={handleSaveAccountEdits}
            onChange={handleAccountFormChange}
            onStrategyChange={(strategyId) =>
              setAccountForm((prev) => ({ ...prev, strategy_id: strategyId }))
            }
          />

          <ApiKeySection
            accountId={account_details.account_id}
            portfolioId={portfolio_id}
            apiKeys={apiKeys}
            selectedApiKey={selectedApiKey}
            newApiKey={newApiKey}
            venueCodes={venueCodes}
            ipAddresses={ipAddresses}
            buttonDisabled={buttonDisableStatus}
            displayForm={displayApiKeyForm}
            onSelectApiKey={setSelectedApiKey}
            onSaveApiKey={handleSaveApiKey}
            onDeleteApiKey={(accountId, keyId) => {
              setDeleteModalStatus({
                open: true,
                item_id: keyId,
                item_name: keyId,
                entity_id: accountId,
              });
            }}
            onNewApiKeyChange={handleNewApiKeyChange}
            onNewApiKeyFreeSoloChange={handleNewApiKeyFreeSoloChange}
            onNewApiKeyVenueApiKeyFlagChange={handleNewApiKeyVenueApiKeyFlagChange}
            onNewApiKeyNamespacesChange={handleNewApiKeyNamespacesChange}
            onNewApiKeyCapabilitiesChange={handleNewApiKeyCapabilitiesChange}
            onShowForm={() => setDisplayApiKeyForm(true)}
            copyToClipboard={copyToClipboard}
          />

          <DeleteModal
            open={deleteModalStatus.open}
            item_name={deleteModalStatus.item_name}
            handleClose={() =>
              setDeleteModalStatus({
                open: false,
                item_id: null,
                item_name: null,
                entity_id: null,
              })
            }
            handleSubmit={() =>
              handleDeleteApiKey(
                deleteModalStatus.entity_id,
                deleteModalStatus.item_id
              )
            }
          />

          <Snackbar
            open={clipboardSnackbar.open}
            autoHideDuration={5000}
            onClose={handleCloseSnackbar}
            resumeHideDuration={0}
          >
            <Alert
              onClose={handleCloseSnackbar}
              severity={clipboardSnackbar.success ? "success" : "error"}
              variant="filled"
              sx={{
                width: "100%",
                backgroundColor: clipboardSnackbar.success
                  ? "#0d732a"
                  : "#8c060d",
              }}
            >
              {clipboardSnackbar.message}
            </Alert>
          </Snackbar>
        </DialogContent>
        <DialogActions className="account-modal-dialog-actions"></DialogActions>
      </CustomDialog>
    </div>
  );
};
export default AccountModal;
